import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import {
  CreatePatientSchema,
  PatientSchema,
  PortalCredentialsSchema,
  UpdatePatientSchema,
  type CreatePatientInput,
  type Patient,
  type PortalCredentials,
  type UpdatePatientInput,
} from '@datazo/contracts';
import { apiFetch } from '@/shared/api/client';

/**
 * Hooks de acceso a `/api/patients`. Cada query/mutation valida la respuesta
 * con el schema Zod compartido — si el backend cambia de forma incompatible
 * el error se manifiesta como `ApiClientError` en el `onError` de React Query.
 */

const PatientListResponseSchema = z.object({
  items: z.array(PatientSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
});

export type PatientListResponse = z.infer<typeof PatientListResponseSchema>;

export interface PatientsListParams {
  page: number;
  pageSize: number;
  q?: string;
  /** Orden `campo:asc|desc`. Hoy se usa `procedimientosCount:asc|desc`. */
  sort?: string;
}

export const patientsKeys = {
  all: ['patients'] as const,
  list: (params: PatientsListParams) => ['patients', 'list', params] as const,
  detail: (id: string) => ['patients', 'detail', id] as const,
  portalCredentials: (id: string) => ['patients', 'portal-credentials', id] as const,
};

export function usePatientsList(
  params: PatientsListParams,
): UseQueryResult<PatientListResponse, Error> {
  return useQuery({
    queryKey: patientsKeys.list(params),
    queryFn: async () => {
      const qs = new URLSearchParams({
        page: String(params.page),
        pageSize: String(params.pageSize),
      });
      if (params.q !== undefined && params.q.trim() !== '') {
        qs.set('q', params.q.trim());
      }
      if (params.sort !== undefined && params.sort !== '') {
        qs.set('sort', params.sort);
      }
      return apiFetch<PatientListResponse>(`/api/patients?${qs.toString()}`, {
        parseAs: PatientListResponseSchema,
      });
    },
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}

export function usePatientById(
  id: string | undefined,
): UseQueryResult<Patient, Error> {
  return useQuery({
    queryKey: id !== undefined ? patientsKeys.detail(id) : ['patients', 'detail', '__missing__'],
    queryFn: async () => {
      if (id === undefined) {
        throw new Error('id requerido');
      }
      return apiFetch<Patient>(`/api/patients/${encodeURIComponent(id)}`, {
        parseAs: PatientSchema,
      });
    },
    enabled: id !== undefined,
    staleTime: 30_000,
  });
}

/** Credenciales del portal de resultados del paciente (usuario + contraseña). */
export function usePortalCredentials(
  id: string | undefined,
): UseQueryResult<PortalCredentials, Error> {
  return useQuery({
    queryKey:
      id !== undefined
        ? patientsKeys.portalCredentials(id)
        : ['patients', 'portal-credentials', '__missing__'],
    queryFn: async () => {
      if (id === undefined) throw new Error('id requerido');
      return apiFetch<PortalCredentials>(
        `/api/patients/${encodeURIComponent(id)}/portal-credentials`,
        { parseAs: PortalCredentialsSchema },
      );
    },
    enabled: id !== undefined,
    staleTime: 30_000,
  });
}

export function useCreatePatient(): UseMutationResult<Patient, Error, CreatePatientInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePatientInput) => {
      const body = CreatePatientSchema.parse(input);
      return apiFetch<Patient>('/api/patients', {
        method: 'POST',
        body,
        parseAs: PatientSchema,
      });
    },
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: patientsKeys.all });
      qc.setQueryData(patientsKeys.detail(created.id), created);
    },
  });
}

export interface UpdatePatientArgs {
  id: string;
  patch: UpdatePatientInput;
}

export function useUpdatePatient(): UseMutationResult<Patient, Error, UpdatePatientArgs> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: UpdatePatientArgs) => {
      const body = UpdatePatientSchema.parse(patch);
      return apiFetch<Patient>(`/api/patients/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body,
        parseAs: PatientSchema,
      });
    },
    onSuccess: (updated) => {
      qc.setQueryData(patientsKeys.detail(updated.id), updated);
      void qc.invalidateQueries({ queryKey: patientsKeys.all });
    },
  });
}
